import {useState,useCallback,useEffect} from 'react'
import {rt} from '../helpers/firebase'
import {ref as rtRef,push,onValue,remove,get} from 'firebase/database'

const MAX_VERSIONS=50
const AUTOSAVE_INTERVAL=5*60*1000 // 5 minutes

export default function useVersionHistory({uid,projectId,nodes,edges,title}) {
    const [versions,setVersions]=useState([])

    // Subscribe to version list
    useEffect(() => {
        if(!uid||!projectId) return
        const versionsRef=rtRef(rt,`versions/${uid}/${projectId}`)
        const unsub=onValue(versionsRef,(snapshot) => {
            if(!snapshot.exists()) {setVersions([]); return}
            const data=snapshot.val()
            const list=Object.entries(data)
                .map(([id,v]) => ({id,...v}))
                .sort((a,b) => b.createdAt-a.createdAt)
            setVersions(list)
        })
        return unsub
    },[uid,projectId])

    const saveVersion=useCallback(async (name=null) => {
        if(!uid||!projectId) return null
        const versionsRef=rtRef(rt,`versions/${uid}/${projectId}`)

        const persistableNodes=nodes.filter(n => n.type!=='reccNode')
        const entry={
            nodes: persistableNodes.map(n => ({...n,selected: false})),
            edges: edges.map(e => ({...e,selected: false})),
            title,
            name: name||`Auto ${new Date().toLocaleString()}`,
            createdAt: Date.now(),
            isManual: !!name
        }

        const newRef=await push(versionsRef,entry)

        // Prune oldest beyond MAX_VERSIONS
        const snap=await get(versionsRef)
        if(snap.exists()) {
            const all=Object.entries(snap.val()).sort((a,b) => a[1].createdAt-b[1].createdAt)
            if(all.length>MAX_VERSIONS) {
                const toDelete=all.slice(0,all.length-MAX_VERSIONS)
                await Promise.all(toDelete.map(([id]) => remove(rtRef(rt,`versions/${uid}/${projectId}/${id}`))))
            }
        }

        return newRef.key
    },[uid,projectId,nodes,edges,title])

    // Auto-snapshot every 5 minutes
    useEffect(() => {
        if(!uid||!projectId) return
        const timer=setInterval(saveVersion,AUTOSAVE_INTERVAL)
        return () => clearInterval(timer)
    },[uid,projectId,saveVersion])

    const restoreVersion=useCallback(async (versionId,{setNodes,setTitle}) => {
        if(!uid||!projectId) return
        const vRef=rtRef(rt,`versions/${uid}/${projectId}/${versionId}`)
        const snap=await get(vRef)
        if(!snap.exists()) return

        // Save current state as a "before restore" checkpoint
        await saveVersion(`Before restore — ${new Date().toLocaleString()}`)

        const version=snap.val()
        setNodes(version.nodes||[])
        if(setTitle&&version.title) setTitle(version.title)
    },[uid,projectId,saveVersion])

    const deleteVersion=useCallback(async (versionId) => {
        if(!uid||!projectId) return
        await remove(rtRef(rt,`versions/${uid}/${projectId}/${versionId}`))
    },[uid,projectId])

    return {versions,saveVersion,restoreVersion,deleteVersion}
}
